import { RAW_CLASSES, type RawClassNode } from './data.generated';
import { ALL_KITS as KITS } from './kits';
import { DEFAULT_BODY, type BodyProfile, type WeaponDef } from './weapons';

export type ClassStatus = 'implemented' | 'planned';

export interface ClassNode extends RawClassNode {
  /** Index réseau stable (ordre du dossier, BASE = 0). */
  index: number;
  status: ClassStatus;
  weapon: WeaponDef;
  body: BodyProfile;
}

const BASE_RAW: RawClassNode = {
  id: 'BASE',
  family: 'Base',
  name: 'Petit tank',
  parent: '',
  level: 1,
  desc: 'tank de départ, un canon',
};

export const CLASSES: ClassNode[] = [BASE_RAW, ...RAW_CLASSES].map((raw, index) => {
  const kit = KITS[raw.id];
  return {
    ...raw,
    index,
    status: kit ? 'implemented' : 'planned',
    weapon: (kit ?? KITS.BASE).weapon,
    body: { ...DEFAULT_BODY, ...(kit?.body ?? {}) },
  };
});

const BY_ID = new Map(CLASSES.map((c) => [c.id, c]));
const CHILDREN = new Map<string, ClassNode[]>();
for (const c of CLASSES) {
  if (!c.parent) continue;
  const list = CHILDREN.get(c.parent) ?? [];
  list.push(c);
  CHILDREN.set(c.parent, list);
}

export function getClass(id: string): ClassNode {
  const c = BY_ID.get(id);
  if (!c) throw new Error(`Classe inconnue : ${id}`);
  return c;
}

export function classByIndex(i: number): ClassNode {
  return CLASSES[i] ?? CLASSES[0];
}

export function childrenOf(id: string): ClassNode[] {
  return CHILDREN.get(id) ?? [];
}

/**
 * Évolutions proposées au niveau donné.
 * Les enfants d'un nœud sont exclusifs : choisir A1 (niv. 50) termine la branche, A2 (niv. 60) exige d'attendre.
 * Seules les classes implémentées sont sélectionnables ; les autres sont visibles dans l'arbre comme « prévues ».
 */
export function availableEvolutions(classId: string, level: number): ClassNode[] {
  return childrenOf(classId).filter((c) => c.level <= level && c.status === 'implemented');
}

export function upcomingEvolutions(classId: string): ClassNode[] {
  return childrenOf(classId);
}

export function isAncestor(ancestorId: string, id: string): boolean {
  let cur = BY_ID.get(id);
  while (cur && cur.parent) {
    if (cur.parent === ancestorId) return true;
    cur = BY_ID.get(cur.parent);
  }
  return false;
}

/** Chemin depuis BASE jusqu'à la classe (incluse). */
export function lineage(id: string): ClassNode[] {
  const path: ClassNode[] = [];
  let cur = BY_ID.get(id);
  while (cur) {
    path.unshift(cur);
    cur = cur.parent ? BY_ID.get(cur.parent) : undefined;
  }
  return path;
}

/** Meilleure classe ancêtre encore légale pour un niveau (utile pour la conservation 75 % en équipes). */
export function bestLegalAncestor(id: string, level: number): ClassNode {
  const path = lineage(id);
  for (let i = path.length - 1; i >= 0; i--) if (path[i].level <= level) return path[i];
  return CLASSES[0];
}
